import React, { useState, useCallback, useEffect } from 'react';
import { PilgrimGuideLink } from '../../../types/pilgrimGuide';
import { Evidence } from '../../../types/pilgrimGuide';
import { PilgrimInfo } from './row/PilgrimInfo';
import { PackageInfo } from './row/PackageInfo';
import { GuideInfo } from './row/GuideInfo';
import { RowActions } from './row/RowActions';
import { EvidenceModal } from '../evidence/EvidenceModal';
import { EvidenceViewModal } from '../evidence/EvidenceViewModal';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { usePilgrimGuideStore } from '../../../stores/pilgrimGuideStore';
import { useLanguage } from '../../../contexts/LanguageContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { firestore } from '../../../lib/firebase/config';
import toast from 'react-hot-toast';

type Props = {
  link: PilgrimGuideLink;
};

export const PilgrimGuideRow = ({ link }: Props) => {
  const [isEvidenceModalOpen, setIsEvidenceModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [evidence, setEvidence] = useState<Evidence | null>(null);
  const [hasSubmittedEvidence, setHasSubmittedEvidence] = useState(false);

  const { translations } = useLanguage();
  const { deleteLink } = usePilgrimGuideStore();
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');

  // Organizers can no longer delete links (prevents creating ghost disputes).
  const canManageLink =
    userRole === 'admin' ||
    (userRole === 'guide' && userId === link.guideId);

  // Disputes + evidence stay open to organizers.
  const canManageEvidence =
    userRole === 'admin' ||
    (userRole === 'organizer' && userId === link.organizerId);

  // Transfer is admin-only now (organizer-triggered transfers caused dupes).
  const showTransfer = userRole === 'admin';

  // Fetch initial evidence data
  useEffect(() => {
    const fetchEvidence = async () => {
      if (link.disputeId) {
        try {
          const disputeDoc = await getDoc(
            doc(firestore, 'Disputes', link.disputeId)
          );
          if (disputeDoc.exists()) {
            const disputeData = disputeDoc.data();
            const guideEvidence = disputeData.disputingGuides?.find(
              (g: any) => (g.organizerId || g.guideId) === link.organizerId
            )?.evidence;
            setHasSubmittedEvidence(Boolean(guideEvidence));
            setEvidence(userRole === 'organizer' ? null : guideEvidence || null);
          }
        } catch (error) {
          console.error('Error fetching evidence:', error);
        }
      }
    };

    fetchEvidence();
  }, [isEvidenceModalOpen, link.disputeId, link.organizerId, userRole]);

  const handleDelete = useCallback(async () => {
    if (!canManageLink) {
      toast.error(
        translations?.PilgrimGuide?.errors?.deletePermission ||
          'You do not have permission to delete this link'
      );
      return;
    }

    setIsDeleting(true);
    try {
      await deleteLink(link.id, translations);
      toast.success(
        translations?.PilgrimGuide?.messages?.deleteSuccess ||
          'Link deleted successfully'
      );
      setIsDeleteConfirmOpen(false);
    } catch (error: any) {
      console.error('Error deleting link:', error);
      toast.error(
        error.message ||
          translations?.PilgrimGuide?.messages?.deleteError ||
          'Failed to delete link'
      );
    } finally {
      setIsDeleting(false);
    }
  }, [link.id, canManageLink, deleteLink, translations]);

  const handleEvidenceModalOpen = useCallback(async () => {
    if (!link.disputeId) {
      toast.error(
        translations?.PilgrimGuide?.errors?.noDispute ||
          'No dispute available to submit evidence'
      );
      return;
    }
    setIsEvidenceModalOpen(true);
  }, [link.disputeId, translations]);

  const handleViewEvidence = useCallback(() => {
    setIsViewModalOpen(true);
  }, []);

  const handleToggleOwnership = useCallback(async () => {
    if (userRole !== 'admin') {
      toast.error('Only admin can change ownership status');
      return;
    }

    try {
      const linkRef = doc(firestore, 'PilgrimGuideLinks', link.id);
      // Use null instead of undefined for Firestore
      const newOwnerStatus = link.isOwner === false ? null : false;
      
      await updateDoc(linkRef, {
        isOwner: newOwnerStatus,
        updatedAt: Date.now()
      });
      
      toast.success(
        newOwnerStatus === false
          ? 'Pilgrim marked as Not Owner'
          : 'Pilgrim marked as Owner'
      );
    } catch (error: any) {
      console.error('Error toggling ownership:', error);
      toast.error(error.message || 'Failed to update ownership status');
    }
  }, [link.id, link.isOwner, userRole]);

  return (
    <tr className="hover:bg-gray-50">
      <PilgrimInfo
        name={link.pilgrimName}
        passportNumber={link.passportNumber}
        gender={link.gender}
        country={link.residenceCountry}
        email={link.email}
        mobileNumber={link.mobileNumber}
      />

      <PackageInfo
        packageName={link.packageName}
        pilgrimType={link.pilgrimType}
        nusukGroup={link.nusukGroup}
      />

      <GuideInfo
        guideName={link.guideName}
        organizerName={link.organizerName}
      />

      <RowActions
        status={link.status}
        evidence={evidence}
        hasEvidence={hasSubmittedEvidence}
        isOwner={link.isOwner}
        onSubmitEvidence={
          canManageEvidence && link.status === 'disputed' && !hasSubmittedEvidence
            ? handleEvidenceModalOpen
            : undefined
        }
        onViewEvidence={
          userRole !== 'organizer' && evidence ? handleViewEvidence : undefined
        }
        onEditEvidence={
          userRole !== 'organizer' &&
          canManageEvidence &&
          link.status === 'disputed' &&
          evidence
            ? handleEvidenceModalOpen
            : undefined
        }
        onDelete={
          canManageLink ? () => setIsDeleteConfirmOpen(true) : undefined
        }
        onToggleOwnership={
          userRole === 'admin' && link.status === 'approved'
            ? handleToggleOwnership
            : undefined
        }
        isDeleting={isDeleting}
        linkId={link.id}
        guideId={link.guideId}
        organizerId={link.organizerId}
        organizerName={link.organizerName}
        pilgrimId={link.pilgrimId}
        pilgrimName={link.pilgrimName}
        showTransfer={showTransfer}
        disputeId={link.disputeId}
      />

      {isEvidenceModalOpen && link.disputeId && (
        <EvidenceModal
          isOpen={true}
          onClose={() => setIsEvidenceModalOpen(false)}
          disputeId={link.disputeId}
          organizerId={link.organizerId}
          existingEvidence={userRole === 'organizer' ? undefined : evidence || undefined}
        />
      )}

      {userRole !== 'organizer' && isViewModalOpen && evidence && (
        <EvidenceViewModal
          isOpen={true}
          onClose={() => setIsViewModalOpen(false)}
          guideName={link.organizerName}
          evidence={evidence}
        />
      )}

      <ConfirmDialog
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title={
          translations?.PilgrimGuide?.confirmDialog?.deleteTitle ||
          'Delete Link'
        }
        message={
          link.status === 'disputed'
            ? translations?.PilgrimGuide?.confirmDialog?.disputeMessage?.replace(
                '{name}',
                link.pilgrimName
              ) ||
              `Deleting this link will end the dispute and give the right to the other guide. Are you sure you want to delete the link for pilgrim "${link.pilgrimName}"?`
            : translations?.PilgrimGuide?.confirmDialog?.defaultMessage?.replace(
                '{name}',
                link.pilgrimName
              ) ||
              `Are you sure you want to delete the link for pilgrim "${link.pilgrimName}"?`
        }
        type="danger"
        confirmText={
          isDeleting
            ? translations?.PilgrimGuide?.confirmDialog?.deleting ||
              'Deleting...'
            : translations?.PilgrimGuide?.confirmDialog?.delete || 'Delete'
        }
        cancelText={
          translations?.PilgrimGuide?.confirmDialog?.cancel || 'Cancel'
        }
      />
    </tr>
  );
};
